'use client';
import { CacheKey } from '@eliadmin/core/src/api/constants';
import { fetchProducts } from '@eliadmin/core/src/api/products/product-api';
import { SignInDialog } from "@eliadmin/core/src/components/dialog/lazy-dialogs";
import { Badge } from '@eliadmin/core/src/components/ui/badge';
import { Button } from '@eliadmin/core/src/components/ui/button';
import { useDialogStore } from '@eliadmin/core/src/components/ui/dialog';
import { HEADER_CONFIG } from '@eliadmin/core/src/constants/header-config';
import { ROUTES } from '@eliadmin/core/src/constants/routes';
import { useCustomerStore } from '@eliadmin/core/src/stores/customer-store';
import { useLocaleStore } from '@eliadmin/core/src/stores/locale-store';
import { cn } from '@eliadmin/core/src/utils/style-utils';
import { useQuery } from '@tanstack/react-query';
import Link from '@eliadmin/core/src/components/link';

export type HeaderFavoritesButtonProps = {
  className?: {
    root?: string;
    info?: string;
    title?: string;
    price?: string;
    badge?: string;
  };
};

export function HeaderFavoritesButton({ className }: HeaderFavoritesButtonProps) {
  const openDialog = useDialogStore(s => s.open);

  const me = useCustomerStore(s => s.me);

  return (
    <Link
      href={ROUTES.FAVORITES()}
      className="flex cursor-pointer flex-col items-center justify-center gap-1 rounded-lg border border-border p-2 transition-all hover:bg-primary/5"
      onClick={e => {
        if (me?.customer) return;
        e.preventDefault();
        e.stopPropagation();
        openDialog({
          id: SignInDialog.id,
          content: <SignInDialog />,
        });
      }}
    >
      <div className="relative">
        <FavoritesBadge />
        {HEADER_CONFIG.headerMain.favorites.icon}
      </div>
      <p className="text-xs">Obľúbené</p>
    </Link>
  );
}

export function FavoritesBadge() {
  const me = useCustomerStore(s => s.me);
  const locale = useLocaleStore(s => s.currentLocale);

  const favoriteItems = useQuery({
    queryKey: [CacheKey.FAVORITE_PRODUCTS, locale, me?.customer.id ?? null],
    queryFn: () =>
      // TODO: add query that fetches only count
      fetchProducts({ locale, authToken: me!.authToken, query: { favorite: true, results: 9999 } }),
    enabled: !!me?.authToken,
  });

  const favoriteItemsCount = favoriteItems.data?.data?.length ?? 0;

  const countText =
    favoriteItemsCount > 99 ? (
      <span>
        99<sup>+</sup>
      </span>
    ) : (
      favoriteItemsCount
    );

  return (
    <Badge
      className={cn(
        'pointer-events-none absolute right-0 top-0 aspect-square h-5 w-5 -translate-y-1/2 translate-x-1/2 justify-center bg-destructive-foreground font-light text-background',
        'ea-favorites-button__badge',
      )}
      size="circle"
    >
      {countText}
    </Badge>
  );
}
